import { useState, useRef, useMemo } from 'react'
import { useApp } from '../../context/AppContext'
import { sileo } from 'sileo'
import { commentService } from '../../services/commentservice'
import { fileService } from '../../services/fileService'
import { uploadService } from '../../services/uploadService'
import useHasAccess from '../../hooks/useHasAccess'
import useUsers from '../../hooks/useUsers'

/**
 * Componente para agregar comentarios a un ticket específico.
 * * Permite redactar mensajes, adjuntar archivos (pre-visualización local) y
 * enviar los datos a la tabla `comment` del esquema `Tickets` en Supabase.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string|number} props.ticketId - Identificador único del ticket al que pertenece el comentario.
 * @param {Object} props.currentUser - Datos del usuario actual para mostrar en el avatar.
 * @param {string} [props.currentUser.name] - Nombre del usuario.
 * @param {Function} [props.onAdded] - Callback opcional que se ejecuta tras insertar con éxito el comentario.
 * Recibe el objeto `payload` enviado.
 * * @returns {JSX.Element} Un área de texto enriquecida con soporte para archivos y envío mediante "Enter".
 */
const CommentaryAdd = ({ ticketId, currentUser, onAdded }) => {
  const { user } = useApp()
  const canInternal = useHasAccess('ticket_support')
  const { allUsers } = useUsers()

  const [loading, setLoading] = useState(false)
  const [content, setContent] = useState('')
  const [files, setFiles] = useState([])
  const [internal, setInternal] = useState(false)

  // Menciones (@): lista seleccionada y estado del autocompletado.
  const [mentions, setMentions] = useState([]) // [{ id, label }]
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionStart, setMentionStart] = useState(0)

  const fileInputRef = useRef(null)
  const inputRef = useRef(null)

  // Sugerencias filtradas por lo escrito tras la @ (máx. 6).
  const suggestions = useMemo(() => {
    if (!mentionOpen) return []
    const q = (mentionQuery || '').toLowerCase()
    return (allUsers || [])
      .filter((u) => u.id_user)
      .filter((u) =>
        `${u.nombre || ''} ${u.apellido || ''}`.toLowerCase().includes(q)
      )
      .slice(0, 6)
  }, [mentionOpen, mentionQuery, allUsers])

  // Detecta si el cursor está escribiendo una mención (@texto sin espacio).
  const detectMention = (value, caret) => {
    const upto = value.slice(0, caret)
    const m = upto.match(/@([\p{L}0-9._-]*)$/u)
    if (m) {
      setMentionQuery(m[1])
      setMentionStart(caret - m[0].length)
      setMentionOpen(true)
    } else {
      setMentionOpen(false)
    }
  }

  const handleContentChange = (e) => {
    const value = e.target.value.trimStart()
    setContent(value)
    detectMention(value, e.target.selectionStart ?? value.length)
  }

  // Inserta la mención elegida reemplazando el "@query" en curso.
  const applyMention = (u) => {
    const label = `${u.nombre || ''} ${u.apellido || ''}`.trim()
    const before = content.slice(0, mentionStart)
    const after = content.slice(mentionStart + 1 + (mentionQuery?.length || 0))
    const newContent = `${before}@${label} ${after}`
    setContent(newContent)
    setMentions((prev) =>
      prev.some((m) => m.id === u.id_user)
        ? prev
        : [...prev, { id: u.id_user, label }]
    )
    setMentionOpen(false)
    // Devolvemos el foco al input.
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  // Abre el menú de usuarios desde el botón "@" (sin tener que escribir @).
  const openMentionMenu = () => {
    const value =
      content.endsWith('@') || content === '' ? content : `${content} `
    const next = `${value}@`
    setContent(next)
    setMentionQuery('')
    setMentionStart(next.length - 1)
    setMentionOpen(true)
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  /**
   * Procesa el envío del comentario a Supabase.
   * Valida que el contenido no esté vacío y gestiona el estado de carga y errores.
   */
  async function handleSubmitAsync() {
    if (!content.trim()) return
    setLoading(true)

    try {
      const {
        success,
        data,
        error: createError,
        // Solo conservamos las menciones cuyo "@etiqueta" sigue en el texto.
      } = await commentService.create({
        ticketId,
        userId: user.id,
        parentCommentId: null,
        content: content.trim(),
        createdAt: new Date().toISOString(),
        deleted: false,
        internal: canInternal ? internal : false,
        mentions: mentions.filter((m) => content.includes(`@${m.label}`)),
      })

      if (!success) throw new Error(createError || 'Failed to create comment')

      for (const file of files) {
        const responUpload = await uploadService.uploadFile(
          file,
          ticketId,
          user.id
        )
        const { success: successInsert, error: errorInsert } =
          await fileService.insert({
            id: null,
            ticketId: ticketId,
            commentId: data.id,
            url: responUpload.url,
            name: file.name,
            mimeType: file.type,
            size: file.size,
            createdAt: new Date().toISOString(),
            solutionId: null,
            uploadedBy: user.id,
          })

        if (!successInsert)
          console.error('Error inserting file record:', errorInsert)
      }

      setContent('')
      setFiles([])
      setMentions([])
      setMentionOpen(false)
      onAdded?.(data)
    } catch (error) {
      console.error(error)
      sileo.error({
        title: 'Error',
        description: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Maneja el evento de teclado para permitir el envío rápido con la tecla Enter.
   * Evita el envío si se presiona Shift + Enter para permitir saltos de línea.
   */
  const handleKeyDown = (e) => {
    // Si el menú de menciones está abierto, Enter elige la primera sugerencia.
    if (mentionOpen && suggestions.length > 0) {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        applyMention(suggestions[0])
        return
      }
      if (e.key === 'Escape') {
        setMentionOpen(false)
        return
      }
    }
    if (e.key === 'Enter' && content.trim() && !e.shiftKey) {
      e.preventDefault()
      handleSubmitAsync()
    }
  }

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files)
    setFiles((prev) => [...prev, ...selected])
  }

  return (
    <div className="w-100">
      {/* Selector Público / Nota interna (solo agentes) */}
      {canInternal && (
        <div className="ca-mode mb-2">
          <button
            type="button"
            className={`ca-mode-btn ${!internal ? 'is-active' : ''}`}
            onClick={() => setInternal(false)}
          >
            <i className="fa-solid fa-globe me-1" />
            Público
          </button>
          <button
            type="button"
            className={`ca-mode-btn ca-mode-internal ${internal ? 'is-active' : ''}`}
            onClick={() => setInternal(true)}
          >
            <i className="fa-solid fa-lock me-1" />
            Nota interna
          </button>
        </div>
      )}

      {/* Pre-visualización de archivos (Utilizando Badges de Bootstrap) */}
      {files.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2 px-1">
          {files.map((f, i) => (
            <span
              key={i}
              className="badge rounded-pill border text-body d-flex align-items-center px-3 py-2 bg-light-subtle"
            >
              <i className="fas fa-file-alt me-2 text-primary"></i>
              <span className="text-truncate" style={{ maxWidth: '120px' }}>
                {f.name}
              </span>
              <i
                className="fas fa-times ms-2 cursor-pointer text-danger"
                onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
              ></i>
            </span>
          ))}
        </div>
      )}

      {/* Contenedor principal con clases de borde y sombra sutil */}
      <div
        className={`p-2 border rounded-4 shadow-sm transition-focus ${
          internal ? 'ca-internal-box' : 'bg-body-tertiary'
        }`}
      >
        <div className="d-flex align-items-center gap-2">
          {/* Avatar con utilidades de Flex y Ratio */}
          <div
            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '32px',
              height: '32px',
              fontSize: '0.8rem',
              fontWeight: '600',
            }}
          >
            {currentUser?.name?.charAt(0).toUpperCase() || (
              <i className="fas fa-user"></i>
            )}
          </div>

          {/* Input sin bordes internos */}
          <div className="flex-grow-1 position-relative">
            <input
              ref={inputRef}
              type="text"
              className="form-control border-0 bg-transparent shadow-none"
              placeholder={
                internal
                  ? 'Nota interna (usa @ para mencionar)…'
                  : 'Escribe un mensaje o respuesta (usa @ para mencionar)...'
              }
              value={content}
              onChange={handleContentChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
              disabled={loading}
              style={{ fontSize: '0.95rem' }}
            />

            {/* Autocompletado de menciones */}
            {mentionOpen && suggestions.length > 0 && (
              <div className="ca-mention-menu">
                {suggestions.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="ca-mention-item"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      applyMention(u)
                    }}
                  >
                    <span className="ca-mention-avatar">
                      {(u.nombre || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="text-truncate">
                      {`${u.nombre || ''} ${u.apellido || ''}`.trim()}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Acciones */}
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn btn-link text-secondary p-2 shadow-none hover-bg-light"
              onClick={openMentionMenu}
              title="Mencionar usuario"
            >
              <i className="fas fa-at"></i>
            </button>

            <button
              type="button"
              className="btn btn-link text-secondary p-2 shadow-none hover-bg-light"
              onClick={() => fileInputRef.current?.click()}
              title="Adjuntar archivos"
            >
              <i className="fas fa-paperclip"></i>
            </button>

            <input
              type="file"
              multiple
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
            />

            <button
              className={`btn rounded-3 d-flex align-items-center justify-content-center p-0 ${
                content.trim() ? 'btn-primary' : 'btn-light text-muted'
              }`}
              style={{ width: '36px', height: '36px' }}
              onClick={handleSubmitAsync}
              disabled={loading || !content.trim()}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                ></span>
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Pequeños ajustes que Bootstrap no cubre por defecto */
        .cursor-pointer { cursor: pointer; }

        .transition-focus:focus-within {
          border-color: var(--bs-primary) !important;
          ring: 0.25rem rgba(var(--bs-primary-rgb), 0.15);
          box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.1) !important;
        }

        .hover-bg-light:hover {
          background-color: var(--bs-secondary-bg);
          border-radius: 8px;
        }

        /* Selector de modo */
        .ca-mode {
          display: inline-flex;
          gap: 4px;
          padding: 3px;
          background: var(--bs-secondary-bg);
          border-radius: 8px;
        }
        .ca-mode-btn {
          border: none;
          background: transparent;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--bs-secondary-color);
        }
        .ca-mode-btn.is-active {
          background: var(--bs-body-bg);
          color: var(--bs-primary);
          font-weight: 600;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        .ca-mode-internal.is-active {
          color: var(--accent);
        }

        /* Caja en modo nota interna (ámbar) */
        .ca-internal-box {
          background: rgba(245, 158, 11, 0.1) !important;
          border-color: rgba(245, 158, 11, 0.45) !important;
        }

        /* Menú de autocompletado de menciones */
        .ca-mention-menu {
          position: absolute;
          bottom: calc(100% + 6px);
          left: 0;
          z-index: 1050;
          min-width: 220px;
          max-height: 230px;
          overflow-y: auto;
          background: var(--bs-body-bg);
          border: 1px solid var(--bs-border-color);
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          padding: 4px;
        }
        .ca-mention-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          border: none;
          background: transparent;
          padding: 6px 8px;
          border-radius: 8px;
          font-size: 0.85rem;
          color: var(--bs-body-color);
          text-align: left;
        }
        .ca-mention-item:hover {
          background: var(--bs-secondary-bg);
        }
        .ca-mention-avatar {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          border-radius: 50%;
          background: var(--bs-primary);
          color: #fff;
          font-size: 0.7rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  )
}

export default CommentaryAdd
